import { forwardRef, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getDqlConfig } from './dql/dql-language.js';
import type { DQLEditorValidityInfo } from './dql-editor-types.js';
import { useValidationCallbacks } from './useValidationCallbacks.js';
import { useValidityInfo } from './useValidityInfo.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { FormControlStateProps } from '../../core/types/form-control-state.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { BaseCodeEditor } from '../base-code-editor/BaseCodeEditor.js';
import type { EditorLanguageConfiguration } from '../base-code-editor/configuration/configuration.js';

const COMPONENT_NAME = 'DQLEditor';

/**
 * @public
 */
export interface DQLEditorProps
  extends FormControlStateProps,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    BehaviorTrackingProps {
  /** The ID for the DOM element. */
  id?: string;
  /**
   * Test id used for matching the editor container.
   * @defaultValue 'dql-editor'
   */
  'data-testid'?: string;
  /** The value (i.e. contents) of the editor.
   * @defaultValue ''
   */
  value?: string;
  /** Handler that is called when the value changes. */
  onChange?: (value: string) => void;
  /** Displayed initially in the editor when there is no other content. */
  placeholder?: string;
  /** Whether spellcheck should be enabled.
   * @defaultValue false
   */
  spellCheck?: boolean;
  /**
   * If set to true, the DQL editor uses the full height available in its parent.
   * @defaultValue false
   */
  fullHeight?: boolean;
  /** The start indices (character position) that should be folded initially in uncontrolled scenarios. */
  defaultFolding?: number[];
  /** The start indices (character position) that should be folded initially in controlled scenarios.*/
  folding?: number[];
  /** Callback that is called when folding changes. */
  onFoldingChange?: (values: number[]) => void;
  /** Whether the input is readonly
   * @defaultValue false
   */
  readOnly?: boolean;
  /** Whether long lines should be wrapped.
   * @defaultValue false
   */
  lineWrap?: boolean;
  /** Callback that is called when the editor loses focus. */
  onBlur?: (e: FocusEvent) => void;
  /** Callback that is called when the editor receives focus. */
  onFocus?: (e: FocusEvent) => void;
  /**
   * Callback fired when DQL validation starts.
   * This can be used for setting a status that validation is in progress.
   */
  onValidationStart?: () => void;
  /**
   * Callback fired when DQL validation ends.
   * This can be used for resetting a status that validation is in progress and to get the validation result.
   */
  onValidationEnd?: (validityInfo: DQLEditorValidityInfo[]) => void;
  /**
   * Callback fired when new diagnostic information is available.
   * This can be used to get the validation result from outside the editor.
   */
  onValidityChange?: (validityInfo: DQLEditorValidityInfo[]) => void;
  /**
   * Editor layout size, 'default' for standard spacing and 'condensed' for reduced font-size, padding and margins.
   * @defaultValue 'default'
   */
  size?: 'default' | 'condensed';
  /**
   * If set to true, `aria-required` will be set to true on the input element.
   * @defaultValue false
   */
  required?: boolean;
}

/**
 * public ref type
 * @public
 */
export type DQLEditorRef = {
  focus: () => void;
  selectRange: (range: { from: number; to: number }) => void;
  readonly element: HTMLDivElement | null;
  setCursorPosition: (position: number | 'end') => void;
};

/**
 * The `DQLEditor` is specifically designed for editing DQL queries. It further offers
 * syntax highlighting and autocomplete functionality specific to the Dynatrace Query Language.
 * Once the editor is focused via the keyboard, the user must press "Enter" to start editing and "Escape" to quit editing and return to the keyboard navigation flow.
 * @public
 */
export const DQLEditor = /* @__PURE__ */ forwardRef<
  DQLEditorRef,
  DQLEditorProps
>((props, forwardedRef) => {
  const intl = useIntl();

  const defaultPlaceholder = intl.formatMessage({
    defaultMessage: 'Add DQL',
    id: 'Ir0YCuBX6vOM4t0J',
    description: 'Default placeholder for the DQLEditor.',
  });

  const {
    id,
    value = '',
    controlState,
    onChange,
    folding,
    onFoldingChange,
    defaultFolding,
    fullHeight = false,
    lineWrap = false,
    placeholder = defaultPlaceholder,
    readOnly = false,
    spellCheck = false,
    onValidationStart,
    onValidationEnd,
    onValidityChange,
    onFocus,
    onBlur,
    size = 'default',
    required,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;

  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

  const [editorConfig, setEditorConfig] =
    useState<EditorLanguageConfiguration>();

  const [fireValidationStart, fireValidationEnd] = useValidationCallbacks(
    onValidationStart,
    onValidationEnd,
  );
  const setValidityInfo = useValidityInfo(onValidityChange);

  useEffect(() => {
    setEditorConfig(
      getDqlConfig(
        readOnly,
        fireValidationStart,
        fireValidationEnd,
        setValidityInfo,
      ),
    );
  }, [readOnly, setValidityInfo, fireValidationStart, fireValidationEnd]);

  return (
    <BaseCodeEditor
      id={id}
      ref={forwardedRef}
      onChange={onChange}
      placeholder={placeholder}
      controlState={controlState}
      readOnly={readOnly}
      defaultFolding={defaultFolding}
      lineWrap={lineWrap}
      value={value}
      spellCheck={spellCheck}
      fullHeight={fullHeight}
      folding={folding}
      onFoldingChange={onFoldingChange}
      configuration={editorConfig}
      onFocus={onFocus}
      onBlur={onBlur}
      size={size}
      required={required}
      className={consumerClassName}
      style={consumerStyle}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      {...ariaLabelingProps}
      {...behavioralTrackingProps}
      {...forwardProps}
      data-dt-component={COMPONENT_NAME}
    />
  );
});
(DQLEditor as typeof DQLEditor & { displayName: string }).displayName =
  COMPONENT_NAME;
